/**
 * SceneManager - Gestor de escena médica con Three.js
 * Encapsula la configuración de cámara, luces y renderizado
 */

import * as THREE from 'three';
import { CSS2DRenderer } from 'three/addons/renderers/CSS2DRenderer.js';
import { OrbitControls } from 'three/addons/controls/OrbitControls.js';
import { VISUAL_CONFIG } from '../core/config';
import state from '../core/state';

// ═══════════════════════════════════════════════════════════════════════════════
// CLASE PRINCIPAL
// ═══════════════════════════════════════════════════════════════════════════════

export class SceneManager {
  // Elementos core
  scene: THREE.Scene;
  camera: THREE.PerspectiveCamera;
  renderer: THREE.WebGLRenderer;
  labelRenderer: CSS2DRenderer;
  controls: OrbitControls;
  
  // Container
  container: HTMLElement;
  
  // Luces
  ambientLight: THREE.AmbientLight;
  directionalLight: THREE.DirectionalLight;
  pointLights: THREE.PointLight[] = [];

  // Estado
  private animationId: number = 0;
  private resizeObserver: ResizeObserver | null = null;

  constructor(container: HTMLElement) {
    this.container = container;
    
    // Crear escena
    this.scene = this.createScene();
    
    // Crear cámara
    this.camera = this.createCamera();
    
    // Crear renderizadores
    this.renderer = this.createRenderer();
    this.labelRenderer = this.createLabelRenderer();
    
    // Crear controles
    this.controls = this.createControls();
    
    // Crear luces
    this.ambientLight = this.createAmbientLight();
    this.directionalLight = this.createDirectionalLight();
    this.scene.add(this.ambientLight);
    this.scene.add(this.directionalLight);
    this.setupPointLights();
    
    // Setup inicial
    this.setupPostProcessing();
    this.setupEventListeners();
    
    // Añadir canvas al DOM
    container.appendChild(this.renderer.domElement);
  }

  // ═════════════════════════════════════════════════════════════════════════════
  // CREACIÓN DE ELEMENTOS
  // ═════════════════════════════════════════════════════════════════════════════
  
  private createScene(): THREE.Scene {
    const scene = new THREE.Scene();
    scene.background = null;

    // Fog sutil para profundidad
    scene.fog = new THREE.FogExp2(0x000000, 0.015);

    return scene;
  }

  private createCamera(): THREE.PerspectiveCamera {
    const camera = new THREE.PerspectiveCamera(
      60,
      this.container.clientWidth / this.container.clientHeight,
      0.1,
      100
    );
    camera.position.set(-1, 0, 14);
    return camera;
  }

  private createRenderer(): THREE.WebGLRenderer {
    const renderer = new THREE.WebGLRenderer({
      antialias: true,
      alpha: true,
      powerPreference: 'high-performance',
    });

    renderer.setSize(this.container.clientWidth, this.container.clientHeight);
    renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
    renderer.setClearColor(0x000000, 0.0);
    
    // Opciones de renderizado
    renderer.shadowMap.enabled = true;
    renderer.shadowMap.type = THREE.PCFSoftShadowMap;
    renderer.toneMapping = THREE.ACESFilmicToneMapping;
    renderer.toneMappingExposure = 1.0;
    
    return renderer;
  }

  private createLabelRenderer(): CSS2DRenderer {
    const renderer = new CSS2DRenderer();
    renderer.setSize(this.container.clientWidth, this.container.clientHeight);
    renderer.domElement.style.position = 'absolute';
    renderer.domElement.style.top = '0';
    renderer.domElement.style.left = '0';
    renderer.domElement.style.pointerEvents = 'none';
    this.container.appendChild(renderer.domElement);
    return renderer;
  }

  private createControls(): OrbitControls {
    const controls = new OrbitControls(this.camera, this.renderer.domElement);
    controls.enableDamping = true;
    controls.dampingFactor = 0.05;
    controls.enableZoom = true;
    controls.enablePan = true;
    controls.minDistance = 2;
    controls.maxDistance = 20;
    controls.target.set(0, 0, 0);
    return controls;
  }

  private createAmbientLight(): THREE.AmbientLight {
    return new THREE.AmbientLight(
      0xffffff,
      VISUAL_CONFIG.AMBIENT_LIGHT_INTENSITY
    );
  }

  private createDirectionalLight(): THREE.DirectionalLight {
    const light = new THREE.DirectionalLight(
      0xffffff,
      VISUAL_CONFIG.DIRECTIONAL_LIGHT_INTENSITY
    );
    light.position.set(5, 10, 7);
    light.castShadow = true;
    
    light.shadow.mapSize.width = 2048;
    light.shadow.mapSize.height = 2048;
    light.shadow.camera.near = 0.5;
    light.shadow.camera.far = 50;
    
    return light;
  }

  private setupPointLights(): void {
    // Luces puntuales para iluminar el espacio médico
    const colors = [0x3B82F6, 0xEF4444, 0x10B981];
    const positions = [
      new THREE.Vector3(-5, 5, 5),
      new THREE.Vector3(5, -5, 5),
      new THREE.Vector3(0, 5, -5),
    ];
    
    positions.forEach((pos, index) => {
      const light = new THREE.PointLight(colors[index], 0.3, 20);
      light.position.copy(pos);
      this.pointLights.push(light);
      this.scene.add(light);
    });
  }

  private setupPostProcessing(): void {
    // Aquí se podría añadir EffectComposer para efectos avanzados
    // Por ahora usamos renderizado estándar
  }

  // ═════════════════════════════════════════════════════════════════════════════
  // EVENT LISTENERS
  // ═════════════════════════════════════════════════════════════════════════════
  
  private setupEventListeners(): void {
    // Resize observer
    this.resizeObserver = new ResizeObserver(() => {
      this.handleResize();
    });
    this.resizeObserver.observe(this.container);
    
    // Teclado para controles
    window.addEventListener('keydown', (e) => this.handleKeyDown(e));
  }

  private handleResize(): void {
    const width = this.container.clientWidth;
    const height = this.container.clientHeight;
    
    this.camera.aspect = width / height;
    this.camera.updateProjectionMatrix();
    
    this.renderer.setSize(width, height);
    this.labelRenderer.setSize(width, height);
  }

  private handleKeyDown(event: KeyboardEvent): void {
    switch (event.key) {
      case 'c':
        this.resetCamera();
        break;
      case 'l':
        state.toggleLabels();
        break;
      case 'e':
        state.toggleEdges();
        break;
      case 'h':
        state.toggleHelp();
        break;
      case ' ': // Espacio para pausar
        event.preventDefault();
        state.togglePause();
        break;
      case '1':
        state.setTopologyMode('anatomical');
        break;
      case '2':
        state.setTopologyMode('fisiopathological');
        break;
      case '3':
        state.setTopologyMode('temporal_spiral');
        break;
      case '4':
        state.setTopologyMode('differential_competitive');
        break;
    }
  }

  // ═════════════════════════════════════════════════════════════════════════════
  // RENDER LOOP
  // ═════════════════════════════════════════════════════════════════════════════
  
  startRenderLoop(
    updateCallback: (deltaTime: number) => void
  ): void {
    let lastTime = performance.now();
    
    const animate = () => {
      this.animationId = requestAnimationFrame(animate);
      
      const currentTime = performance.now();
      const deltaTime = Math.min((currentTime - lastTime) / 1000, 0.1); // Limitar a 100ms
      lastTime = currentTime;
      
      // Actualizar lógica
      updateCallback(deltaTime);
      
      // Actualizar controles
      this.controls.update();
      
      // Renderizar
      this.render();
    };
    
    animate();
  }

  stopRenderLoop(): void {
    cancelAnimationFrame(this.animationId);
  }

  render(): void {
    this.renderer.render(this.scene, this.camera);
    this.labelRenderer.render(this.scene, this.camera);
  }

  // ═════════════════════════════════════════════════════════════════════════════
  // CONTROLES DE CÁMARA
  // ═════════════════════════════════════════════════════════════════════════════
  
  resetCamera(): void {
    this.camera.position.set(-1, 0, 14);
    this.camera.lookAt(0, 0, 0);
    this.controls.target.set(0, 0, 0);
    this.controls.update();
  }

  focusOnPoint(point: THREE.Vector3, distance: number = 5): void {
    // Animar cámara hacia punto
    const startPos = this.camera.position.clone();
    const endPos = point.clone().add(new THREE.Vector3(0, 0, distance));
    const duration = 500;
    const startTime = performance.now();
    
    const animate = () => {
      const elapsed = performance.now() - startTime;
      const progress = Math.min(elapsed / duration, 1);
      const eased = 1 - Math.pow(1 - progress, 3); // Ease out cubic
      
      this.camera.position.lerpVectors(startPos, endPos, eased);
      this.controls.target.lerp(point, eased);
      this.controls.update();
      
      if (progress < 1) {
        requestAnimationFrame(animate);
      }
    };
    
    animate();
  }

  zoomToNode(nodePosition: THREE.Vector3): void {
    this.focusOnPoint(nodePosition, 3);
  }

  // ═════════════════════════════════════════════════════════════════════════════
  // UTILIDADES
  // ═════════════════════════════════════════════════════════════════════════════
  
  getSceneSize(): { width: number; height: number } {
    return {
      width: this.container.clientWidth,
      height: this.container.clientHeight,
    };
  }

  projectToScreen(position: THREE.Vector3): { x: number; y: number } {
    const projected = position.clone().project(this.camera);
    const { width, height } = this.getSceneSize();
    
    return {
      x: (projected.x + 1) * width / 2,
      y: (-projected.y + 1) * height / 2,
    };
  }

  raycastFromScreen(x: number, y: number): THREE.Ray {
    const mouse = new THREE.Vector2(
      (x / this.container.clientWidth) * 2 - 1,
      -(y / this.container.clientHeight) * 2 + 1
    );
    
    const raycaster = new THREE.Raycaster();
    raycaster.setFromCamera(mouse, this.camera);
    
    return raycaster.ray;
  }

  // ═════════════════════════════════════════════════════════════════════════════
  // LIMPIEZA
  // ═════════════════════════════════════════════════════════════════════════════
  
  dispose(): void {
    this.stopRenderLoop();
    this.resizeObserver?.disconnect();
    
    this.renderer.dispose();
    this.labelRenderer.domElement.remove();
    
    // Limpiar geometrías y materiales
    this.scene.traverse((object) => {
      if (object instanceof THREE.Mesh) {
        object.geometry.dispose();
        if (Array.isArray(object.material)) {
          object.material.forEach(m => m.dispose());
        } else {
          object.material.dispose();
        }
      }
    });
  }
}

export default SceneManager;
